import React, { useState, useRef, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { compressImage } from "@/lib/image";
import {
  Image as ImageIcon, PenTool, Film, Video, Loader2, Download, Sparkles, Upload, Building2, Copy,
  CheckCircle2, Wand2, Pencil, QrCode,
} from "lucide-react";
import api, { formatError } from "@/lib/api";
import { downloadFile, mediaUrl, saveBlob } from "@/lib/download";
import { copyText as clipCopy } from "@/lib/clipboard";
import { StudioVoiceBox } from "@/components/StudioVoiceBox";
import ImageEditor from "@/components/ImageEditor";
import { ResultRating } from "@/components/ResultRating";
import { useProjects } from "@/context/ProjectContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import NoProject from "@/components/NoProject";
import JourneyBar from "@/components/JourneyBar";
import { toast } from "sonner";
import { fiveTags } from "@/lib/hashtags";
import { GenProgress } from "@/components/GenProgress";
import { CharacterPicker } from "@/components/CharacterPicker";
import { ProductPicker } from "@/components/ProductPicker";
import { ExtraImagesPicker } from "@/components/ExtraImagesPicker";
import { ProductCatalog } from "@/components/ProductCatalog";
import { AddCreditsButton } from "@/components/AddCreditsButton";
import { StudioProjectBar } from "@/components/StudioProjectBar";
import { DesignFields, designSummary } from "@/components/DesignFields";
import { BrandGuide } from "@/components/BrandGuide";
import { LogoConcepts } from "@/components/LogoConcepts";
import { CardLayoutPicker } from "@/components/CardLayoutPicker";
import { PaletteVariants } from "@/components/PaletteVariants";
import { LogoStylePicker } from "@/components/LogoStylePicker";
import { WordmarkFontPicker } from "@/components/WordmarkFontPicker";
import { IndividualHub, kindSizes, kindPrintable } from "@/components/IndividualHub";
import { AdBriefFields, EMPTY_BRIEF, briefMissing } from "@/components/AdBriefFields";
import { DeleteX } from "@/components/DeleteX";
import { OverlayEditor } from "@/components/OverlayEditor";
import { MoodSelect, withMood } from "@/components/MoodSelect";
import { track as funnelTrack } from "@/lib/funnel";

const delContent = async (id, after) => {
  try { await api.delete(`/content/${id}`); after(); toast.success("تم الحذف"); }
  catch (err) { toast.error(formatError(err.response?.data?.detail)); }
};

const PLATFORMS = ["Instagram", "TikTok", "Snapchat", "Twitter"];

const FORMATS = {
  Instagram: ["منشور مربع 1:1", "ستوري / ريلز 9:16", "كاروسيل 4:5"],
  TikTok: ["فيديو 9:16", "غلاف 9:16"],
  Snapchat: ["سناب 9:16", "ستوري 9:16"],
  Twitter: ["منشور 16:9", "صورة غلاف 3:1"],
};

function Field({ label, children, className = "" }) {
  return <div className={`min-w-0 ${className}`}><Label className="mb-2 block">{label}</Label>{children}</div>;
}

export default function ContentStudio() {
  const { activeProject, refresh } = useProjects();
  const [params, setParams] = useSearchParams();
  const [studioProject, setStudioProject] = useState(null);
  const [mode, setModeState] = useState(() => localStorage.getItem("rawaj_studio_mode") || "");  // يُستعاد عند العودة ليكمل أي توليد جارٍ
  const setMode = (m) => { setModeState(m); localStorage.setItem("rawaj_studio_mode", m || ""); };
  const [itemKind, setItemKindState] = useState(() => localStorage.getItem("rawaj_studio_kind") || "");  // التوليد الفردي: القطعة المختارة — تُستعاد عند العودة
  const setItemKind = (k) => { setItemKindState(k); localStorage.setItem("rawaj_studio_kind", k || ""); };
  const [flash, setFlash] = useState(0);  // تظليل الحقول المطلوبة بالبرتقالي بعد اختيار النوع
  useEffect(() => { if (!flash) return; const t = setTimeout(() => setFlash(0), 6000); return () => clearTimeout(t); }, [flash]);
  const [brand, setBrand] = useState({ name: "", logo: null, info: "" });
  const [brandOpen, setBrandOpen] = useState(false);  // هوية العلامة مطوية افتراضيًا في التوليد الفردي — تُفتح عند الحاجة
  const [extras, setExtras] = useState([]);
  const [product, setProduct] = useState({ image: "", name: "" });
  const individual = !activeProject || params.get("individual") === "1";  // مسار «توليد فردي» مستقل — لا يرتبط بأي مشروع
  const [studioList, setStudioList] = useState([]);  // مشاريع التوليد الفردي المسمّاة (كلٌّ يحتفظ بمدخلاته)
  const [studioLoaded, setStudioLoaded] = useState(false);
  const [studioId, setStudioIdState] = useState(() => localStorage.getItem("rawaj_studio_pid") || "");
  const setStudioId = (id) => { setStudioIdState(id); localStorage.setItem("rawaj_studio_pid", id || ""); };
  useEffect(() => { const sp = params.get("sp"); if (sp && sp !== studioId) setStudioId(sp); }, [params]); // eslint-disable-line react-hooks/exhaustive-deps
  const pickStudio = (id) => { setStudioId(id); const q = new URLSearchParams(params); q.set("individual", "1"); q.set("sp", id); setParams(q, { replace: true }); };
  const collection = individual ? (studioProject?.name || "") : "";  // يُحفظ المحتوى في المكتبة باسم مشروع التوليد الفردي
  const eff = individual ? studioProject : activeProject;

  useEffect(() => {
    if (eff) setProduct({ image: eff.product_image || "", name: eff.product_name || "" });
  }, [eff?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveProduct = async (patch) => {
    const next = { ...product, ...patch };
    setProduct(next);
    try {
      await api.patch(`/projects/${eff.id}/meta`, { product_image: next.image || "", product_name: next.name || "" });
      if (activeProject) refresh();
      if (patch.image !== undefined) toast.success(next.image ? "تم حفظ صورة المنتج للمشروع" : "تمت إزالة صورة المنتج");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  useEffect(() => {
    if (!individual) { setBrand({ name: activeProject.name || "", logo: activeProject.logo || null, info: "", colors: activeProject.colors || [] }); return; }
    // Individual track → named personal studio projects (never tied to a client project); inputs persist per project.
    api.get("/me/studio-projects").then((r) => setStudioList(r.data)).catch(() => {}).finally(() => setStudioLoaded(true));
  }, [activeProject, individual]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!individual || !studioList.length) return;
    const p = studioList.find((x) => x.id === studioId) || studioList[0];
    if (p.id !== studioId) setStudioId(p.id);
    setStudioProject(p);
    setBrand({ name: p.brand_name || "", logo: p.logo || null, info: p.brand_info || "", colors: p.colors || [] });
    setBrandOpen(!p.brand_name && !p.logo);  // أول مرة (بلا اسم ولا شعار) تظهر الحقول مباشرة، وإلا تبقى مطوية
  }, [studioList, studioId, individual]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveBrand = async (patch) => {  // حفظ مدخلات العلامة في مشروع التوليد الفردي لأي استخدام مستقبلي
    if (!individual || !studioProject) return;
    try {
      const { data } = await api.patch(`/projects/${studioProject.id}/meta`, patch);
      setStudioList((xs) => xs.map((x) => (x.id === data.id ? data : x)));
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  if (individual && studioLoaded && !studioList.length) return (  // لا مشروع بعد → يجب إنشاء مشروع باسم أولًا ليُحفظ العمل ويبدأ التوليد
    <div className="space-y-6" data-testid="studio-page">
      <div>
        <h1 className="text-3xl font-extrabold font-heading">التوليد الفردي</h1>
        <p className="text-muted-foreground">خدمات حسب الطلب — بدون اشتراك: صورة، شعار، بطاقة عمل، منيو، إنفوجرافيك، بروشور، فيديو — والنصوص مجانية</p>
      </div>
      <StudioProjectBar list={[]} value="" required onChange={pickStudio} onCreated={(p) => { setStudioList([p]); pickStudio(p.id); }} />
    </div>
  );
  if (!eff) return (
    <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
  );

  const onBrandLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("حجم الشعار يجب أن يكون أقل من 2 ميجابايت");
    const reader = new FileReader();
    reader.onload = () => { setBrand((b) => ({ ...b, logo: reader.result })); saveBrand({ logo: reader.result }); };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-6 overflow-x-hidden" data-testid="studio-page">
      {(eff.standalone || eff.has_strategy_ready) && !individual && <JourneyBar current="content" />}
      <div>
        <h1 className="text-3xl font-extrabold font-heading">{individual ? "التوليد الفردي" : "استوديو المحتوى"}</h1>
        <p className="text-muted-foreground">{individual
          ? "خدمات حسب الطلب — بدون اشتراك: صورة، شعار، بطاقة عمل، منيو، إنفوجرافيك، بروشور، فيديو، أو مسار مركّب (نص منشور + صورة / سكربت + فيديو) — والنصوص مجانية"
          : `توليد الصور والنصوص والسكربتات والفيديو لـ «${eff.name}»`}</p>
        {individual && <Link to={`/app/library?individual=1${studioId ? `&sp=${studioId}` : ""}`} className="inline-flex items-center gap-1 text-xs text-primary underline underline-offset-2 mt-1" data-testid="individual-library-link">مكتبة التوليد الفردي (مستقلة عن المشاريع)</Link>}
      </div>
      {individual && <StudioProjectBar list={studioList} value={studioId} onChange={pickStudio} onCreated={(p) => { setStudioList((xs) => [...xs, p]); pickStudio(p.id); }} />}
      {individual && <IndividualHub selected={itemKind} selectedMode={mode} onPick={(it) => {
        setItemKind(it.kind);
        setMode(it.mode || (it.group === "video" ? "script-video" : "image"));
        setFlash(Date.now());
        setTimeout(() => document.getElementById("gen-form")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
      }} />}

      {!(individual && itemKind === "logo") && (
      <div className="glass rounded-2xl p-6 border border-border" data-testid="brand-info-panel">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Building2 className="w-5 h-5 text-primary" />
          <h2 className="font-bold">هوية العلامة</h2>
          {individual && !brandOpen && (
            <span className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="brand-summary">
              {brand.logo && <img src={brand.logo} alt="" className="w-7 h-7 rounded-md object-contain border border-border bg-white/5" />}
              <span>{brand.name || "بلا اسم بعد"}{product.image ? " · صورة المنتج ✓" : ""}</span>
              {brand.logo && brand.colors?.length > 0 && (
                <span className="flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-2 py-0.5" title="ألوان شعارك المعتمد تُستخدم تلقائيًا في كل تصميم" data-testid="brand-colors-auto">
                  {brand.colors.slice(0, 4).map((c) => <span key={c} className="w-3 h-3 rounded-full border border-black/10" style={{ background: c }} />)}
                  <span>ألوان الشعار تلقائيًا</span>
                </span>
              )}
            </span>
          )}
          <span className="text-xs text-muted-foreground ms-auto hidden sm:inline">تُضاف تلقائياً على التصاميم</span>
          {individual && (
            <button type="button" onClick={() => setBrandOpen((v) => !v)} className="text-xs text-primary underline" data-testid="brand-toggle">
              {brandOpen ? "طيّ" : brand.name || brand.logo ? "تعديل" : "أضف الاسم والشعار"}
            </button>
          )}
        </div>
        {individual && brandOpen && <p className="text-[11px] text-primary mb-3" data-testid="brand-persist-note">تُحفظ هذه المدخلات تلقائيًا في مشروع «{studioProject?.name}» وتُستعاد عند اختياره مستقبلًا.</p>}
        {(!individual || brandOpen) && (<>
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="الاسم التجاري">
            <Input value={brand.name} onChange={(e) => setBrand({ ...brand, name: e.target.value })} onBlur={() => saveBrand({ brand_name: brand.name })} className="bg-secondary border-border" data-testid="brand-name-input" />
          </Field>
          <div>
            <Label className="mb-2 block">الشعار</Label>
            <div className="flex items-center gap-3">
              <label className="cursor-pointer flex-1">
                <input type="file" accept="image/*" className="hidden" onChange={onBrandLogo} data-testid="brand-logo-input" />
                <div className="flex items-center gap-2 justify-center rounded-xl border border-dashed border-border bg-secondary py-2.5 text-sm text-muted-foreground hover:border-primary/40 transition-colors">
                  <Upload className="w-4 h-4" /> {brand.logo ? "تغيير" : "رفع الشعار"}
                </div>
              </label>
              {brand.logo && <img src={brand.logo} alt="logo" className="w-11 h-11 rounded-lg object-contain border border-border bg-white/5" data-testid="brand-logo-preview" />}
            </div>
          </div>
          <Field label="معلومات إضافية على التصميم">
            <Input value={brand.info} onChange={(e) => setBrand({ ...brand, info: e.target.value })} onBlur={() => saveBrand({ brand_info: brand.info })} placeholder="عرض، رقم جوال، موقع، شعار نصي" className="bg-secondary border-border" data-testid="brand-info-input" />
          </Field>
        </div>
        <div className="mt-4 pt-4 border-t border-border">
          {itemKind !== "business_card" && <ProductPicker image={product.image} name={product.name} testId="studio-product"
            onImage={(v) => saveProduct({ image: v })}
            onName={(v) => setProduct((p) => ({ ...p, name: v }))} />}
          {!individual && <div className="mt-4 flex justify-end"><AddCreditsButton /></div>}
          {itemKind !== "business_card" && <div className="mt-4"><ExtraImagesPicker value={extras} onChange={setExtras} label="صور إضافية تخدم المحتوى" testId="studio-extras" /></div>}
          <div className="mt-4">
            <ProductCatalog projectId={eff?.id} onChange={() => {}} />
            <p className="text-[11px] text-muted-foreground mt-1">لاستعراض منتج محدد في هذا التوليد: ارفع صورته أعلاه في «صورة المنتج» أو اختره من حملاتك — المنتجات المضافة هنا تُستعرض تلقائيًا في الحملات.</p>
          </div>
          {product.image && (
            <button type="button" onClick={() => saveProduct({})} className="text-xs text-primary mt-1" data-testid="studio-product-name-save">حفظ اسم المنتج</button>
          )}
        </div>
        </>)}
      </div>
      )}

      <div className={`rounded-2xl border p-3 transition-colors ${mode ? "border-border" : "border-red-500/70 guide-glow"}`} data-testid="studio-mode-picker">
        <div className="flex items-center gap-2 mb-2 px-1">
          <span className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center ${mode ? "bg-accent/15 text-accent border border-accent/40" : "bg-red-500/15 text-red-400 border border-red-500/40"}`}>1</span>
          <p className={`text-sm font-bold ${mode ? "text-muted-foreground" : "text-red-400"}`} data-testid="studio-mode-hint">
            {mode ? "نوع التوليد المختار — يمكنك تغييره في أي وقت" : "اختر نوع التوليد أولاً لتظهر لك الحقول المطلوبة"}
          </p>
        </div>
      <div id="gen-form" className={`rounded-2xl transition-shadow duration-500 ${flash ? "ring-4 ring-inset ring-amber-500/70" : ""}`} data-testid="gen-form" data-highlight={flash ? "1" : "0"}>
      <Tabs value={mode} onValueChange={setMode}>
        {individual && mode && <div className="text-xs text-muted-foreground mb-2 px-1" data-testid="individual-step-hint">٣ · املأ المدخلات ← ٤ · اختر المقاس والصيغة ← ٥ · ولّد. لتغيير النوع ارجع للأعلى.</div>}
        <TabsList className={`bg-secondary flex-wrap h-auto gap-1 ${individual ? "hidden" : ""}`}>
          <TabsTrigger value="post-image" data-testid="tab-post-image" className="gap-2"><Wand2 className="w-4 h-4" /> منشور + صورة</TabsTrigger>
          <TabsTrigger value="script-video" data-testid="tab-script-video" className="gap-2"><Wand2 className="w-4 h-4" /> سكربت + فيديو</TabsTrigger>
          <TabsTrigger value="image" data-testid="tab-image" className="gap-2"><ImageIcon className="w-4 h-4" /> صورة</TabsTrigger>
          <TabsTrigger value="caption" data-testid="tab-caption" className="gap-2"><PenTool className="w-4 h-4" /> منشور نصي</TabsTrigger>
          <TabsTrigger value="script" data-testid="tab-script" className="gap-2"><Film className="w-4 h-4" /> سكربت فيديو</TabsTrigger>
          <TabsTrigger value="video" data-testid="tab-video" className="gap-2"><Video className="w-4 h-4" /> فيديو AI</TabsTrigger>
        </TabsList>
        <TabsContent value="post-image"><PostImageFlow projectId={eff.id} brand={brand} extras={extras} collection={collection} /></TabsContent>
        <TabsContent value="script-video"><ScriptVideoFlow projectId={eff.id} brand={brand} extras={extras} collection={collection} product={product} onProduct={saveProduct} /></TabsContent>
        <TabsContent value="image"><ImageTab projectId={eff.id} brand={brand} extras={extras} itemKind={individual ? itemKind : ""} collection={collection} onAdoptLogo={(logo, colors) => { setBrand((b) => ({ ...b, logo, colors: colors?.length ? colors : b.colors })); if (!individual) refresh(); }} /></TabsContent>
        <TabsContent value="caption"><CaptionTab projectId={eff.id} collection={collection} /></TabsContent>
        <TabsContent value="script"><ScriptTab projectId={eff.id} collection={collection} /></TabsContent>
        <TabsContent value="video"><VideoTab projectId={eff.id} brand={brand} extras={extras} product={product} onProduct={saveProduct} /></TabsContent>
      </Tabs>
      </div>
      </div>
    </div>
  );
}

function StepBadge({ n, label, done, active }) {
  return (
    <div className={`flex items-center gap-2 ${active ? "text-primary" : done ? "text-accent" : "text-muted-foreground"}`}>
      <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold border ${done ? "bg-accent/15 border-accent/40" : active ? "bg-primary/15 border-primary/40" : "border-border"}`}>
        {done ? <CheckCircle2 className="w-4 h-4" /> : n}
      </span>
      <span className="text-sm font-semibold">{label}</span>
    </div>
  );
}

function PostImageFlow({ projectId, brand, extras = [], collection = "" }) {
  const [topic, setTopic] = useState("");
  const [platform, setPlatform] = useState("Instagram");
  const [format, setFormat] = useState(FORMATS["Instagram"][0]);
  const [tone, setTone] = useState("احترافية");
  const [mood, setMood] = useState("");
  const [genText, setGenText] = useState(false);
  const [postText, setPostText] = useState("");
  const [imgLoading, setImgLoading] = useState(false);
  const [job, setJob] = useState(null);
  const [image, setImage] = useState(null);
  const [imageId, setImageId] = useState(null);
  const [editing, setEditing] = useState(false);
  const pollRef = useRef(null);
  const trackImg = (id) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/content/image/${id}/status`);
        setJob((j) => ({ ...(j || { start: Date.now() }), progress: r.data.progress || 0, stage: r.data.stage || "scene", queue_position: r.data.queue_position }));
        if (r.data.status === "completed") { clearInterval(pollRef.current); setImage(r.data.data); setImageId(id); setImgLoading(false); window.dispatchEvent(new Event("rawaj:usage-changed")); toast.success("تمت الصورة ✅ — محفوظة في مكتبة المحتوى"); }
        else if (r.data.status === "failed") { clearInterval(pollRef.current); setImgLoading(false); toast.error(r.data.error || "فشل توليد الصورة"); }
      } catch (e) {}
    }, 2000);
  };
  useEffect(() => {
    resumeImage(projectId, {
      onProcessing: (live) => { if (live.caption) setPostText(live.caption); setImgLoading(true); setJob({ start: new Date(live.created_at).getTime() || Date.now(), progress: live.progress || 0, stage: live.stage || "scene" }); trackImg(live.id); },
      onCompleted: (full) => { if (full.caption) { setPostText(full.caption); setImage(full.data); setImageId(full.id); } },
    });
    return () => clearInterval(pollRef.current);
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const generateText = async () => {
    if (!topic) return toast.error("أدخل موضوع المنشور");
    setGenText(true); setPostText(""); setImage(null);
    try {
      const { data } = await api.post("/content/caption", { project_id: projectId, topic, platform, tone, collection });
      const t = data.text || {};
      const combined = [t.caption, t.cta, fiveTags(t.hashtags).join(" ")].filter(Boolean).join("\n\n");
      setPostText(combined);
      toast.success("تم توليد النص — راجعه وعدّله ثم اعتمده");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setGenText(false); }
  };

  const approveAndGenerateImage = async () => {
    if (!postText.trim()) return toast.error("ولّد النص أولاً");
    if (!mood) return toast.error("اختر الأجواء المطلوبة للصورة");
    setImgLoading(true); setImage(null); setJob({ start: Date.now(), progress: 0, stage: "scene" });
    try {
      const { data } = await api.post("/content/image", {
        project_id: projectId, prompt: withMood(topic, mood), caption: postText,
        platform, content_format: format, item_kind: "post_image", collection,
        brand_name: brand?.name || "", overlay_text: brand?.info || "", logo: brand?.logo || null, extras,
      });
      toast.info("جارٍ توليد الصورة المطابقة للنص… تُحفظ في مكتبة المحتوى ويستمر التوليد حتى لو غادرت الصفحة");
      trackImg(data.id);
    } catch (err) { setImgLoading(false); toast.error(formatError(err.response?.data?.detail)); }
  };

  const copyText = async () => {
    if (await clipCopy(postText)) toast.success("تم نسخ النص للنشر ✅"); else toast.error("تعذّر النسخ، حدّد النص وانسخه يدوياً");
  };

  return (
    <div className="mt-6 space-y-4" data-testid="post-image-flow">
      <div className="glass rounded-2xl p-4 border border-border flex items-center gap-4 flex-wrap">
        <StepBadge n={1} label="توليد النص" active={!postText} done={!!postText} />
        <span className="text-muted-foreground">←</span>
        <StepBadge n={2} label="اعتماد وتوليد الصورة" active={!!postText && !image} done={!!image} />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="glass rounded-2xl p-6 border border-border space-y-4">
          <Field label="موضوع المنشور">
            <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="مثال: عرض نهاية الأسبوع على القهوة المختصة" className="bg-secondary border-border" data-testid="pi-topic-input" />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="المنصة">
              <Select value={platform} onValueChange={(v) => { setPlatform(v); setFormat(FORMATS[v][0]); }}>
                <SelectTrigger className="bg-secondary border-border" data-testid="pi-platform-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border">{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="الصيغة">
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger className="bg-secondary border-border" data-testid="pi-format-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border">{(FORMATS[platform] || []).map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="النبرة">
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger className="bg-secondary border-border" data-testid="pi-tone-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="احترافية">احترافية</SelectItem>
                  <SelectItem value="ودية">ودية</SelectItem>
                  <SelectItem value="حماسية">حماسية</SelectItem>
                  <SelectItem value="فاخرة">فاخرة</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <MoodSelect value={mood} onChange={setMood} testId="pi-mood-select" label="أجواء الصورة" />
          <Button onClick={generateText} disabled={genText} variant="outline" className="w-full gap-2 border-border" data-testid="pi-generate-text-btn">
            {genText ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenTool className="w-4 h-4" />} {postText ? "إعادة توليد النص" : "توليد النص + الهاشتاقات"}
          </Button>

          {postText && (
            <div className="space-y-3 pt-2 border-t border-border" data-testid="pi-text-block">
              <Label className="block">راجع النص وعدّله قبل الاعتماد</Label>
              <Textarea value={postText} onChange={(e) => setPostText(e.target.value)} rows={7} className="bg-secondary border-border leading-relaxed" data-testid="pi-text-editable" />
              <div className="flex gap-2">
                <Button onClick={copyText} variant="outline" className="gap-2 border-border flex-1" data-testid="pi-copy-text-btn"><Copy className="w-4 h-4" /> نسخ النص</Button>
                <Button onClick={approveAndGenerateImage} disabled={imgLoading} className="gap-2 bg-primary hover:bg-primary/90 text-white flex-1" data-testid="pi-approve-image-btn">
                  {imgLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} اعتمد وولّد الصورة
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="glass rounded-2xl p-6 border border-border flex items-center justify-center min-h-[320px]">
          {imgLoading ? (
            <div className="text-center w-full"><Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" /><p className="text-sm text-muted-foreground mb-4">جارٍ توليد الصورة المطابقة للنص…</p><GenProgress kind="image" job={job} /></div>
          ) : image ? (
            <div className="w-full space-y-3" data-testid="pi-image-result">
              <div className="relative">
                <img src={`data:image/png;base64,${image}`} alt="generated" className="rounded-xl w-full border border-border" />
                {imageId && <DeleteX testId="pi-delete" className="absolute top-2 end-2" onConfirm={() => delContent(imageId, () => { setImage(null); setImageId(null); })} />}
              </div>
              {imageId && <OverlayEditor contentId={imageId} testId="pi-overlay" className="w-full" onUpdated={(d) => d.data && setImage(d.data)} />}
              <div className="grid grid-cols-3 gap-2">
                <Button onClick={() => setEditing(true)} variant="outline" className="w-full gap-2 border-border" data-testid="pi-edit-image-btn"><Pencil className="w-4 h-4" /> تحرير</Button>
                <a href={`data:image/png;base64,${image}`} download="image.png" onClick={(e) => { e.preventDefault(); downloadFile(`data:image/png;base64,${image}`, "image.png"); }}><Button variant="outline" className="w-full gap-2 border-border"><Download className="w-4 h-4" /> تحميل</Button></a>
                <Button onClick={copyText} variant="outline" className="w-full gap-2 border-border" data-testid="pi-copy-text-btn-2"><Copy className="w-4 h-4" /> نسخ النص</Button>
              </div>
              <p className="text-xs text-muted-foreground text-center">حرّر الصورة لإضافة الشعار والنصوص ثم نزّلها، وانسخ النص للنشر</p>
              {imageId && <ResultRating product="post_image" contentId={imageId} projectId={projectId} />}
            </div>
          ) : (
            <div className="text-center text-muted-foreground"><ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-40" /><p className="text-sm">ولّد النص أولاً ثم اعتمده لتظهر الصورة هنا</p></div>
          )}
        </div>
      </div>
      <ImageEditor open={editing} item={{ data: image, title: topic }} projectLogo={brand?.logo} brandName={brand?.name} onClose={() => setEditing(false)} />

    </div>
  );
}

// متابعة توليد الفيديو: التوليد يستمر في الخادم حتى لو خرج العميل من الصفحة — عند العودة نلتقط آخر فيديو قيد المعالجة ونكمل عرض تقدّمه
function useVideoWatch(pollRef, { setStatus, setVideoUrl, setVideoId, setVoiceName, setIdea, setJob }) {
  return (id) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/content/video/${id}/status`);
        setJob((j) => ({ ...(j || { start: Date.now() }), progress: r.data.progress || 0, stage: r.data.stage || "scene", queue_position: r.data.queue_position }));
        if (r.data.status === "completed") {
          clearInterval(pollRef.current);
          if (r.data.voice_warning) toast.warning(r.data.voice_warning, { duration: 12000 });
          setStatus("completed"); setVideoUrl(r.data.url); setVideoId(id); setVoiceName([r.data.voice_name, r.data.voice_style].filter(Boolean).join(" · ")); setIdea(r.data.director_notes?.creative_idea || "");
          toast.success("اكتمل توليد الفيديو 🎬 — محفوظ في مكتبة المحتوى");
        } else if (r.data.status === "failed") {
          clearInterval(pollRef.current);
          setStatus("failed");
          toast.error(r.data.error || "فشل توليد الفيديو");
        }
      } catch (e) {}
    }, 2000);
  };
}

/** يستأنف آخر صورة للمشروع عند العودة للصفحة: الجارية تُتابَع، والمكتملة حديثًا تُعرض — لا يضيع أي توليد */
async function resumeImage(projectId, { onProcessing, onCompleted }) {
  try {
    const { data } = await api.get("/content", { params: { type: "image", ...(projectId ? { project_id: projectId } : {}) } });
    const items = (data || []).filter((x) => x.in_project !== true);
    const live = items.find((x) => x.status === "processing");
    if (live) { toast.info("صورة قيد التوليد — نكمل متابعتها هنا"); return onProcessing(live); }
    const recent = items.find((x) => x.status === "completed" && Date.now() - new Date(x.created_at).getTime() < 20 * 60 * 1000);
    if (recent) {
      const full = await api.get(`/content/${recent.id}`);
      if (full.data?.data) onCompleted(full.data);
    }
  } catch (e) {}
}

async function resumeVideo(projectId, watch, setStatus, setJob) {
  try {
    const { data } = await api.get("/content", { params: { type: "video", ...(projectId ? { project_id: projectId } : {}) } });
    const live = (data || []).find((x) => x.status === "processing");
    if (live) {
      setStatus("processing");
      setJob({ start: new Date(live.created_at).getTime() || Date.now(), progress: live.progress || 0, stage: live.stage || "scene" });
      toast.info("فيديو قيد التوليد — نكمل متابعته هنا");
      watch(live.id);
    }
  } catch (e) {}
}

/** خانة صورة المنتج داخل كل مسار فيديو: المنتج نفسه يظهر في الإعلان ويمكن تداوله — نفس التجربة في كل المسارات */
const ProductPanel = ({ product, onProduct, testId }) => (
  <div className="rounded-xl border border-dashed border-primary/40 bg-primary/5 p-3" data-testid={`${testId}-panel`}>
    <ProductPicker image={product?.image || ""} name={product?.name || ""} testId={testId}
      onImage={(v) => onProduct({ image: v })} onName={(v) => onProduct({ name: v })} />
    <p className="text-[11px] text-muted-foreground mt-1.5">{product?.image ? "منتجك الحقيقي سيظهر في مشاهد الفيديو ويُحفظ للمشروع لكل التوليدات القادمة." : "ارفع صورة منتجك ليظهر هو نفسه (لونه وتصميمه) في الإعلان بدل منتج متخيّل — تُحفظ للمشروع."}</p>
  </div>
);

function ScriptVideoFlow({ projectId, brand, extras = [], collection = "", product = {}, onProduct }) {
  const [topic, setTopic] = useState("");
  const [vtype, setVtype] = useState("إعلان منتج");
  const [aspect, setAspect] = useState("9:16");
  const [duration, setDuration] = useState("8");
  const [genText, setGenText] = useState(false);
  const [scriptText, setScriptText] = useState("");
  const [gender, setGender] = useState("male");
  const [pick, setPick] = useState({ animGender: "male", voice: "", style: "" });
  const [brief, setBrief] = useState(EMPTY_BRIEF);
  const [voiceName, setVoiceName] = useState("");
  const [voiceReady, setVoiceReady] = useState(false);
  const [idea, setIdea] = useState("");
  const [videoId, setVideoId] = useState(null);
  const [status, setStatus] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);
  const watch = useVideoWatch(pollRef, { setStatus, setVideoUrl, setVideoId, setVoiceName, setIdea, setJob });
  useEffect(() => { resumeVideo(projectId, watch, setStatus, setJob); return () => clearInterval(pollRef.current); }, [projectId]); // eslint-disable-line

  const generateScript = async () => {
    if (!topic) return toast.error("أدخل موضوع الفيديو");
    const miss = briefMissing(brief);
    if (miss.length) return toast.error(`أكمل تفاصيل الإعلان: ${miss.join("، ")}`);
    setGenText(true); setScriptText(""); setVideoUrl(null); setStatus(null);
    try {
      const { data } = await api.post("/content/script", { project_id: projectId, topic, video_type: vtype, ad_brief: brief, collection });
      const s = data.text || {};
      const scenes = (s.scenes || []).map((sc) => [sc.visual && `المشهد${sc.timestamp ? ` (${sc.timestamp})` : ""}: ${sc.visual}`, sc.voiceover && `التعليق الصوتي: ${sc.voiceover}`].filter(Boolean).join("\n")).join("\n\n");
      const combined = [s.title && `العنوان: ${s.title}`, s.hook && `الافتتاحية: ${s.hook}`, scenes, s.cta && `الختام: ${s.cta}`, s.music_suggestion && `الموسيقى: ${s.music_suggestion}`].filter(Boolean).join("\n\n");
      setScriptText(combined);
      toast.success("تم توليد السكربت — راجعه وعدّله ثم اعتمده");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setGenText(false); }
  };

  const approveAndGenerateVideo = async (premium = false) => {
    if (!scriptText.trim()) return toast.error("ولّد السكربت أولاً");
    const miss = briefMissing(brief);
    if (miss.length) return toast.error(`أكمل تفاصيل الإعلان: ${miss.join("، ")}`);
    setStatus("processing"); setVideoUrl(null); setJob({ start: Date.now(), progress: 0, stage: "scene" });
    try {
      const { data } = await api.post("/content/video", {
        project_id: projectId, prompt: topic, caption: scriptText, premium: premium === true, gender, ad_brief: brief, item_kind: "script_video", collection,
        voice: pick.voice, voice_style: pick.style, anim_gender: gender === "animation" ? pick.animGender : "",
        aspect_ratio: aspect, resolution: "720p", duration: parseInt(duration),
        brand_name: brand?.name || "", overlay_text: brand?.info || "", logo: brand?.logo || null, extras,
      });
      toast.info("بدأ توليد الفيديو بناءً على السكربت، قد يستغرق عدة دقائق…");
      watch(data.id);
    } catch (err) { setStatus("failed"); toast.error(formatError(err.response?.data?.detail)); }
  };

  const copyText = async () => {
    if (await clipCopy(scriptText)) toast.success("تم نسخ السكربت ✅"); else toast.error("تعذّر النسخ، حدّد النص وانسخه يدوياً");
  };

  return (
    <div className="mt-6 space-y-4" data-testid="script-video-flow">
      <div className="glass rounded-2xl p-4 border border-border flex items-center gap-4 flex-wrap">
        <StepBadge n={1} label="توليد السكربت" active={!scriptText} done={!!scriptText} />
        <span className="text-muted-foreground">←</span>
        <StepBadge n={2} label="اعتماد وتوليد الفيديو" active={!!scriptText && status !== "completed"} done={status === "completed"} />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="glass rounded-2xl p-6 border border-border space-y-4">
          {onProduct && <ProductPanel product={product} onProduct={onProduct} testId="sv-product" />}
          <Field label="موضوع الفيديو">
            <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="مثال: تقديم القهوة المختصة الجديدة" className="bg-secondary border-border" data-testid="sv-topic-input" />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="نوع الفيديو">
              <Select value={vtype} onValueChange={setVtype}>
                <SelectTrigger className="bg-secondary border-border" data-testid="sv-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="إعلان منتج">إعلان منتج</SelectItem>
                  <SelectItem value="شرح منتج">شرح منتج</SelectItem>
                  <SelectItem value="عرض ترويجي">عرض ترويجي</SelectItem>
                  <SelectItem value="قصة علامة تجارية">قصة علامة تجارية</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="الأبعاد">
              <Select value={aspect} onValueChange={setAspect}>
                <SelectTrigger className="bg-secondary border-border" data-testid="sv-aspect-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border"><SelectItem value="9:16">9:16 عمودي</SelectItem><SelectItem value="16:9">16:9 أفقي</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="المدة (ث)">
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger className="bg-secondary border-border" data-testid="sv-duration-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border">{[4, 6, 8].map((d) => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <AdBriefFields value={brief} onChange={setBrief} testId="sv-brief" />
          <Button onClick={generateScript} disabled={genText} variant="outline" className="w-full gap-2 border-border" data-testid="sv-generate-script-btn">
            {genText ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4" />} {scriptText ? "إعادة توليد السكربت" : "توليد السكربت"}
          </Button>

          {scriptText && (
            <div className="space-y-3 pt-2 border-t border-border" data-testid="sv-text-block">
              <Label className="block">راجع السكربت وعدّله قبل الاعتماد</Label>
              <Textarea value={scriptText} onChange={(e) => setScriptText(e.target.value)} rows={8} className="bg-secondary border-border leading-relaxed" data-testid="sv-text-editable" />
              <CharacterPicker value={gender} onChange={setGender} pick={pick} onPick={setPick} testId="sv-character" productName={`${brief?.product_name || ""} ${brief?.product_type || ""}`.trim()}
                previewRequest={(v, s) => api.post("/content/voice-preview", { project_id: projectId, voice: v, style: s || "", script: scriptText }, { responseType: "blob", timeout: 180000 })} />
              <StudioVoiceBox projectId={projectId} text={scriptText} voice={pick.voice} style={pick.style} free onReady={setVoiceReady} onChangeText={setScriptText} testId="sv" />
              <div className="flex gap-2">
                <Button onClick={copyText} variant="outline" className="gap-2 border-border flex-1" data-testid="sv-copy-text-btn"><Copy className="w-4 h-4" /> نسخ السكربت</Button>
                <Button onClick={() => approveAndGenerateVideo(false)} disabled={status === "processing" || !voiceReady} title={voiceReady ? "" : "جرّب الصوت واحفظه أولاً"} className="gap-2 bg-primary hover:bg-primary/90 text-white flex-1 disabled:opacity-50" data-testid="sv-approve-video-btn">
                  {status === "processing" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} ٣· اعتمد وولّد الفيديو
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="glass rounded-2xl p-6 border border-border flex items-center justify-center min-h-[320px]">
          {status === "processing" ? (
            <div className="text-center w-full"><Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" /><p className="text-sm text-muted-foreground mb-4">جارٍ إنتاج الفيديو بناءً على السكربت…</p><GenProgress kind="video" job={job} /></div>
          ) : status === "completed" && videoUrl ? (
            <div className="w-full space-y-3" data-testid="sv-video-result">
              <div className="relative">
                <video src={mediaUrl(videoUrl)} controls playsInline className="rounded-xl w-full border border-border" />
                {videoId && <DeleteX testId="sv-delete" className="absolute top-2 end-2" onConfirm={() => delContent(videoId, () => { setVideoUrl(null); setVideoId(null); setStatus(null); })} />}
              </div>
              {videoId && <OverlayEditor contentId={videoId} testId="sv-overlay" className="w-full" onUpdated={(d) => d.url && setVideoUrl(d.url)} />}
              {voiceName && <p className="text-xs text-center text-muted-foreground" data-testid="sv-voice-name">بصوت: <span className="text-primary font-bold">{voiceName}</span></p>}
              {idea && <p className="text-[11px] text-center text-muted-foreground" data-testid="sv-director-idea">فكرة المخرج: {idea}</p>}
              <div className="grid grid-cols-2 gap-2">
                <a href={videoUrl} onClick={(e) => { e.preventDefault(); downloadFile(videoUrl, "video.mp4"); }}><Button variant="outline" className="w-full gap-2 border-border"><Download className="w-4 h-4" /> فتح / تحميل</Button></a>
                <Button onClick={copyText} variant="outline" className="w-full gap-2 border-border" data-testid="sv-copy-text-btn-2"><Copy className="w-4 h-4" /> نسخ السكربت</Button>
              </div>
              <p className="text-xs text-muted-foreground text-center">انسخ النص وأرفقه عند رفع الفيديو على قناتك</p>
              {videoId && <ResultRating product="script_video" contentId={videoId} projectId={projectId} />}
            </div>
          ) : status === "failed" ? (
            <div className="text-center text-destructive"><Video className="w-12 h-12 mx-auto mb-2 opacity-50" /><p className="text-sm">تعذّر توليد الفيديو، حاول مجدداً</p></div>
          ) : (
            <div className="text-center text-muted-foreground"><Video className="w-12 h-12 mx-auto mb-2 opacity-40" /><p className="text-sm">ولّد السكربت أولاً ثم اعتمده ليظهر الفيديو هنا</p></div>
          )}
        </div>
      </div>
    </div>
  );
}

const NO_MOOD_KINDS = ["logo", "business_card"];  // أصول هوية — لا تحتاج اختيار أجواء
const KIND_PLACEHOLDER = {
  logo: "مثال: شعار لمقهى قهوة مختصة باسم «دندنة»، بسيط وعصري بلون بني وذهبي",
  business_card: "مثال: بطاقة عمل لمكتب استشارات هندسية — الاسم: م. خالد، جوال، موقع إلكتروني",
  cover: "مثال: غلاف صفحة لمتجر عطور فاخر بلون أسود وذهبي مع مساحة للعنوان",
  menu: "مثال: منيو مقهى: قهوة اليوم 12، لاتيه 16، آيس أمريكانو 14، كرواسون 9",
  infographic: "مثال: إنفوجرافيك عن 5 فوائد للتسويق بالفيديو للمتاجر الصغيرة",
  brochure: "مثال: بروشور ثلاثي الطي لشركة عقارية يعرض 3 مشاريع ومزايا التمويل",
};

function ImageTab({ projectId, brand, extras = [], itemKind = "", collection = "", onAdoptLogo }) {
  const [pdfBusy, setPdfBusy] = useState(false);
  const [qp] = useSearchParams();
  const libSp = qp.get("individual") === "1" ? (qp.get("sp") || "") : "";  // رابط مكتبة مشروع التوليد الفردي
  const special = itemKind && itemKind !== "image";  // قطعة من الكتالوج — مقاسها وصيغة إخراجها تُختار هنا بدل القناة
  const [size, setSize] = useState("");
  const [output, setOutput] = useState("png");
  const [design, setDesign] = useState({});  // مدخلات القطعة المتعارف عليها بالسوق — تُنسَّق داخل التصميم نفسه
  const draftKey = `rawaj_draft_${projectId}_${itemKind || "image"}`;  // تُحفظ المدخلات محليًا لتبقى عند مغادرة الصفحة والعودة
  useEffect(() => {
    let d = {};
    try { d = JSON.parse(localStorage.getItem(draftKey) || "{}"); } catch {}
    setSize(special ? kindSizes(itemKind)[0] : ""); setOutput("png"); setDesign(d.design || {}); setPrompt(d.prompt || "");
  }, [itemKind, projectId]); // eslint-disable-line
  const designInputs = () => Object.fromEntries(Object.entries(design).filter(([, v]) => (v || "").trim()));
  const effPrompt = () => prompt || (special ? designSummary(itemKind, design) : "");
  const [prompt, setPrompt] = useState("");
  useEffect(() => { localStorage.setItem(draftKey, JSON.stringify({ design, prompt })); }, [design, prompt, draftKey]);
  const [platform, setPlatform] = useState("Instagram");
  const [format, setFormat] = useState(FORMATS["Instagram"][0]);
  const [mood, setMood] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [editing, setEditing] = useState(false);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);
  const track = (item) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/content/image/${item.id}/status`);
        setJob((j) => ({ ...(j || { start: Date.now() }), progress: r.data.progress || 0, stage: r.data.stage || "scene", queue_position: r.data.queue_position }));
        if (r.data.status === "completed") {
          clearInterval(pollRef.current);
          setResult({ ...item, data: r.data.data, brand_guide: r.data.brand_guide });
          funnelTrack("gen_view", itemKind || "image");
          setLoading(false);
          window.dispatchEvent(new Event("rawaj:usage-changed"));
          toast.success("تم توليد الصورة ✅ — محفوظة في مكتبة المحتوى");
        } else if (r.data.status === "failed") {
          clearInterval(pollRef.current); setLoading(false);
          toast.error(r.data.error || "فشل توليد الصورة");
        }
      } catch (e) {}
    }, 2000);
  };
  useEffect(() => {
    resumeImage(projectId, {
      onProcessing: (live) => { setLoading(true); setJob({ start: new Date(live.created_at).getTime() || Date.now(), progress: live.progress || 0, stage: live.stage || "scene" }); track(live); },
      onCompleted: (full) => { setResult(full); if (full.prompt && !prompt) setPrompt(full.prompt); },
    });
    return () => clearInterval(pollRef.current);
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const gen = async () => {
    if (!effPrompt()) return toast.error(special ? "عبّئ حقول الخدمة (الاسم/العنوان على الأقل) أو اكتب وصفًا" : "أدخل وصف الصورة");
    if (!mood && !NO_MOOD_KINDS.includes(itemKind)) return toast.error("اختر الأجواء المطلوبة للصورة");
    setLoading(true); setResult(null); setJob({ start: Date.now(), progress: 0, stage: "scene" });
    funnelTrack("gen_request", itemKind || "image");
    try {
      const { data } = await api.post("/content/image", { project_id: projectId, prompt: withMood(effPrompt(), mood), platform, content_format: format, brand_name: brand?.name || "", overlay_text: brand?.info || "", logo: brand?.logo || null, extras, item_kind: itemKind || "image", collection, size: special ? size : "", output: special ? output : "png", design_inputs: designInputs() });
      toast.info("جارٍ توليد الصورة… تُحفظ تلقائيًا في مكتبة المحتوى ويستمر التوليد حتى لو غادرت الصفحة");
      track(data);
    } catch (err) {
      setLoading(false);
      toast.error(formatError(err.response?.data?.detail) || "فشل توليد الصورة");
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 mt-6 min-w-0">
      <div className="glass rounded-2xl p-4 sm:p-6 border border-border space-y-4 min-w-0 overflow-hidden">
        {special && <DesignFields kind={itemKind} value={design} onChange={setDesign} />}
        {itemKind === "business_card" && <CardLayoutPicker projectId={projectId} brand={brand?.name} inputs={design} onPick={(k) => setDesign((d) => ({ ...d, _layout: k }))} onPalette={(p, base) => setDesign((d) => ({ ...d, _palette: p, _base_palette: base }))} />}
        {itemKind === "logo" && <LogoStylePicker value={design.logo_style || ""} onChange={(k) => setDesign((d) => ({ ...d, logo_style: k }))} />}
        {itemKind === "logo" && <WordmarkFontPicker value={design.wordmark_font || "bold"} brand={design.brand_name || brand?.name} color={(design.concept_palette || "").split(",")[1]?.trim() || (design.concept_palette || "").split(",")[0]?.trim()} onChange={(k) => setDesign((d) => ({ ...d, wordmark_font: k }))} />}
        {itemKind === "logo" && <LogoConcepts projectId={projectId} brand={brand?.name} inputs={design} onPick={(c) => setDesign((d) => ({ ...d, ...c }))} />}
        {special && !["logo", "business_card"].includes(itemKind) && brand?.colors?.length > 0 && (
          <PaletteVariants base={brand.colors} selected={design._palette ? design._palette.split(",").map((s) => s.trim()) : []} testid="product-palette-variants"
            onPick={(cols) => setDesign((d) => ({ ...d, _palette: cols.join(", ") }))} />
        )}
        <Field label={special ? "وصف إضافي للخدمة (اختياري — المشهد، الألوان، ما تريد إبرازه)" : "وصف الصورة (بالعربية) *"}>
          <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4}
            placeholder={KIND_PLACEHOLDER[itemKind] || "مثال: فنجان قهوة مختصة على طاولة خشبية مع إضاءة دافئة، أسلوب احترافي"} className="bg-secondary border-border" data-testid="image-prompt-input" data-optional={special ? "" : undefined} />
          {!special && <p className="text-[11px] text-muted-foreground mt-1">إذا أضفت صورة منتجك في «صورة المنتج» أعلاه سيظهر المنتج نفسه (لونه وتصميمه) في الصورة، والشعار يُضاف تلقائياً.</p>}
        </Field>
        {!NO_MOOD_KINDS.includes(itemKind) && <MoodSelect value={mood} onChange={setMood} testId="image-mood-select" />}
        <div className={`grid grid-cols-2 gap-3 ${special ? "hidden" : ""}`}>
          <Field label="المنصة">
            <Select value={platform} onValueChange={(v) => { setPlatform(v); setFormat(FORMATS[v][0]); }}>
              <SelectTrigger className="bg-secondary border-border" data-testid="image-platform-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover border-border">{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="الصيغة (حسب القناة)">
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger className="bg-secondary border-border" data-testid="image-format-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover border-border">
                {(FORMATS[platform] || []).map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
        {special && (
          <div className="rounded-lg bg-primary/10 border border-primary/30 px-3 py-2 space-y-2" data-testid="image-kind-note">
            <p className="text-xs">٤ · المقاس والصيغة — الخدمة المختارة: <b>{itemKind === "logo" ? "لوقو" : itemKind === "business_card" ? "بطاقة عمل" : itemKind === "cover" ? "غلاف / بانر" : itemKind === "menu" ? "منيو / قائمة أسعار" : itemKind === "infographic" ? "إنفوجرافيك" : "بروشور"}</b> — تُخصم خدمة واحدة من رصيدك عند بدء التوليد وتُعاد تلقائيًا إذا فشل.</p>
            <div className="grid grid-cols-2 gap-2 min-w-0">
              <Field label="المقاس">
                <Select value={size} onValueChange={setSize}>
                  <SelectTrigger className="bg-background border-border h-9" data-testid="image-size-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">{kindSizes(itemKind).map((r) => <SelectItem key={r} value={r} data-testid={`image-size-${r.replace(":", "x")}`}>{r === "1:1" ? "مربع 1:1" : r === "4:5" ? "عمودي 4:5" : r === "9:16" ? "ستوري 9:16" : "عرضي 16:9"}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="صيغة الإخراج">
                <Select value={output} onValueChange={setOutput} disabled={!kindPrintable(itemKind)}>
                  <SelectTrigger className="bg-background border-border h-9" data-testid="image-output-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="png" data-testid="image-output-png">صورة PNG</SelectItem>
                    {kindPrintable(itemKind) && <SelectItem value="pdf" data-testid="image-output-pdf">PDF للطباعة</SelectItem>}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </div>
        )}
        <Button onClick={gen} disabled={loading} className="w-full bg-primary hover:bg-primary/90 text-white gap-2" data-testid="content-generate-image-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {special ? "توليد الخدمة" : "توليد الصورة"}
        </Button>
      </div>
      <div className="glass rounded-2xl p-6 border border-border flex items-center justify-center min-h-[320px]">
        {loading ? (
          <div className="text-center w-full"><Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" /><p className="text-sm text-muted-foreground mb-4">جارٍ توليد الصورة…</p><GenProgress kind="image" job={job} /></div>
        ) : result ? (
          <div className="w-full" data-testid="image-result">
            <div className="relative mb-3">
              <img src={`data:image/png;base64,${result.data}`} alt="generated" className="rounded-xl w-full border border-border" />
              {result.id && <DeleteX testId="image-delete" className="absolute top-2 end-2" onConfirm={() => delContent(result.id, () => setResult(null))} />}
            </div>
            {result.id && !special && <OverlayEditor contentId={result.id} testId="image-overlay" className="w-full mb-2" onUpdated={(d) => d.data && setResult((r) => ({ ...r, data: d.data }))} />}
            <div className="grid grid-cols-2 gap-2" data-testid="result-actions">
              <Button className="w-full gap-2 bg-primary text-white hover:bg-primary/90" data-testid="image-download-png" onClick={() => downloadFile(`data:image/png;base64,${result.data}`, `${itemKind || "image"}.png`)}><Download className="w-4 h-4" /> تحميل PNG</Button>
              {special && kindPrintable(itemKind) && result.id ? (
                <Button className="w-full gap-2 bg-primary text-white hover:bg-primary/90" data-testid="image-download-pdf" disabled={pdfBusy}
                  title={itemKind === "business_card" ? "وجهان · ٨ بطاقات على ورقة A4 بعلامات قص" : itemKind === "brochure" ? "A4 عرضي بخطوط الطيّ" : "A4 بدقة طباعة"}
                  onClick={async () => { setPdfBusy(true); try { const r = await api.get(`/content/${result.id}/pdf`, { responseType: "blob" }); await saveBlob(r.data, `${itemKind}-A4.pdf`); } catch (e) { toast.error("تعذّر تجهيز PDF"); } setPdfBusy(false); }}>
                  {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} PDF للطباعة
                </Button>
              ) : (
                <Button onClick={() => setEditing(true)} variant="outline" className="w-full gap-2 border-border" data-testid="image-edit-btn"><Pencil className="w-4 h-4" /> تحرير</Button>
              )}
              {itemKind === "business_card" && result.id && (
                <a href={`/card/${result.id}`} target="_blank" rel="noreferrer" className="col-span-2 inline-flex items-center justify-center gap-2 rounded-md border border-emerald-500/50 text-emerald-700 dark:text-emerald-400 h-10 text-sm font-semibold" data-testid="digital-card-open"><QrCode className="w-4 h-4" /> البطاقة الرقمية — يفتحها رمز QR: شاركها واحفظ جهة الاتصال</a>
              )}
              {itemKind === "logo" && result.id && (
                <Button className="col-span-2 w-full gap-2 h-auto py-2 whitespace-normal" variant="outline" data-testid="logo-pack-download"
                  onClick={async () => { try { const r = await api.get(`/content/${result.id}/logo-pack`, { responseType: "blob" }); await saveBlob(r.data, "logo-pack.zip"); } catch (e) { toast.error("تعذّر تجهيز حزمة اللوقو"); } }}><Download className="w-4 h-4" /> حزمة اللوقو ZIP — شفاف + أبيض + أسود + ملف الهوية</Button>
              )}
              {itemKind === "logo" && result.id && (
                <Button variant="outline" className="col-span-2 w-full gap-2 border-emerald-500/40 text-emerald-700 dark:text-emerald-400" data-testid="logo-use-as-project"
                  onClick={async () => { try { const { data } = await api.post(`/content/${result.id}/use-as-logo`); onAdoptLogo?.(data.logo, data.colors); toast.success("اعتُمد شعارًا للمشروع — سيظهر تلقائيًا في بطاقات العمل والمنتجات القادمة"); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } }}>
                  <CheckCircle2 className="w-4 h-4" /> اعتمده شعارًا لهذا المشروع
                </Button>
              )}
            </div>
            {result.id && (
              <p className="mt-2 text-[11px] text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2 leading-relaxed" data-testid="library-forever-note">
                حُفظت في <Link to={`/app/library${libSp ? `?sp=${libSp}` : ""}`} className="underline underline-offset-2 font-semibold">مكتبة المحتوى</Link> — عدّل البيانات والصورة الشخصية والنصوص ونزّل PNG/PDF في أي وقت بلا حدود وبلا رصيد.
              </p>
            )}
            {result.id && <ResultRating product={itemKind || "image"} contentId={result.id} projectId={projectId} />}
            {itemKind === "logo" && <BrandGuide guide={result.brand_guide} />}
          </div>
        ) : (
          <div className="text-center text-muted-foreground"><ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-40" /><p className="text-sm">ستظهر الصورة المولّدة هنا</p></div>
        )}
      </div>
      <ImageEditor open={editing} item={{ data: result?.data, title: prompt }} projectLogo={brand?.logo} brandName={brand?.name} onClose={() => setEditing(false)} allowLogo={itemKind !== "logo"} fileName={itemKind || "image"} />

    </div>
  );
}

function CaptionTab({ projectId, collection = "" }) {
  const [topic, setTopic] = useState("");
  const [platform, setPlatform] = useState("Instagram");
  const [tone, setTone] = useState("احترافية");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const gen = async () => {
    if (!topic) return toast.error("أدخل موضوع المنشور");
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/content/caption", { project_id: projectId, topic, platform, tone, collection });
      setResult(data.text);
      toast.success("تم توليد المنشور");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 mt-6">
      <div className="glass rounded-2xl p-6 border border-border space-y-4">
        <Field label="موضوع المنشور"><Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="مثال: عرض نهاية الأسبوع" className="bg-secondary border-border" data-testid="caption-topic-input" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="المنصة">
            <Select value={platform} onValueChange={setPlatform}><SelectTrigger className="bg-secondary border-border" data-testid="caption-platform-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover border-border">{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label="نبرة الكتابة">
            <Select value={tone} onValueChange={setTone}><SelectTrigger className="bg-secondary border-border" data-testid="caption-tone-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover border-border">
                <SelectItem value="احترافية">احترافية</SelectItem>
                <SelectItem value="ودية">ودية</SelectItem>
                <SelectItem value="حماسية">حماسية</SelectItem>
                <SelectItem value="فاخرة">فاخرة</SelectItem>
              </SelectContent></Select>
          </Field>
        </div>
        <Button onClick={gen} disabled={loading} className="w-full bg-primary hover:bg-primary/90 text-white gap-2" data-testid="content-generate-caption-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} توليد المنشور
        </Button>
      </div>
      <div className="glass rounded-2xl p-6 border border-border min-h-[320px]">
        {loading ? <div className="flex items-center justify-center h-full"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        : result ? (
          <div data-testid="caption-result" className="space-y-4">
            <p className="text-sm leading-relaxed whitespace-pre-line">{result.caption}</p>
            {result.cta && <p className="text-sm font-semibold text-accent">{result.cta}</p>}
            {result.hashtags && <div className="flex flex-wrap gap-2">{fiveTags(result.hashtags).map((h, i) => <span key={i} className="text-xs bg-primary/15 text-primary px-2 py-1 rounded-full">{h}</span>)}</div>}
            <Button variant="outline" className="w-full gap-2 border-border" data-testid="copy-caption-btn"
              onClick={async () => {
                const txt = [result.caption, result.cta, fiveTags(result.hashtags).join(" ")].filter(Boolean).join("\n\n");
                if (await clipCopy(txt)) toast.success("تم نسخ المحتوى والهاشتاقات ✅"); else toast.error("تعذّر النسخ، حدّد النص وانسخه يدوياً");
              }}>
              <Copy className="w-4 h-4" /> نسخ المحتوى + الهاشتاقات
            </Button>
            <ResultRating product="caption" contentId={result.id || result.caption?.slice(0, 40) || ""} projectId={projectId} />
          </div>
        ) : <div className="flex items-center justify-center h-full text-center text-muted-foreground"><div><PenTool className="w-12 h-12 mx-auto mb-2 opacity-40" /><p className="text-sm">سيظهر المنشور المولّد هنا</p></div></div>}
      </div>
    </div>
  );
}

function ScriptTab({ projectId, collection = "" }) {
  const [topic, setTopic] = useState("");
  const [vtype, setVtype] = useState("إعلان منتج");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const gen = async () => {
    if (!topic) return toast.error("أدخل موضوع الفيديو");
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/content/script", { project_id: projectId, topic, video_type: vtype, collection });
      setResult(data.text);
      toast.success("تم توليد السكربت");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 mt-6">
      <div className="glass rounded-2xl p-6 border border-border space-y-4">
        <Field label="موضوع الفيديو"><Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="مثال: تقديم منتج القهوة الجديد" className="bg-secondary border-border" data-testid="script-topic-input" /></Field>
        <Field label="نوع الفيديو">
          <Select value={vtype} onValueChange={setVtype}><SelectTrigger className="bg-secondary border-border" data-testid="script-type-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover border-border">
              <SelectItem value="إعلان منتج">إعلان منتج</SelectItem>
              <SelectItem value="شرح منتج">شرح منتج</SelectItem>
              <SelectItem value="عرض ترويجي">عرض ترويجي</SelectItem>
              <SelectItem value="قصة علامة تجارية">قصة علامة تجارية</SelectItem>
            </SelectContent></Select>
        </Field>
        <Button onClick={gen} disabled={loading} className="w-full bg-primary hover:bg-primary/90 text-white gap-2" data-testid="content-generate-script-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} توليد السكربت
        </Button>
      </div>
      <div className="glass rounded-2xl p-6 border border-border min-h-[320px]">
        {loading ? <div className="flex items-center justify-center h-full"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        : result ? (
          <div data-testid="script-result" className="space-y-3">
            <h3 className="font-bold text-lg">{result.title}</h3>
            {result.hook && <div className="bg-accent/10 border border-accent/20 rounded-lg p-3"><span className="text-xs text-accent font-bold">الافتتاحية: </span><span className="text-sm">{result.hook}</span></div>}
            <div className="space-y-2">
              {(result.scenes || []).map((sc, i) => (
                <div key={i} className="bg-secondary rounded-lg p-3">
                  <div className="text-xs text-primary font-mono mb-1">{sc.timestamp}</div>
                  <div className="text-sm"><span className="font-semibold">مشهد: </span>{sc.visual}</div>
                  <div className="text-sm text-muted-foreground"><span className="font-semibold">تعليق: </span>{sc.voiceover}</div>
                </div>
              ))}
            </div>
            {result.cta && <p className="text-sm font-semibold text-accent">الختام: {result.cta}</p>}
            {result.music_suggestion && <p className="text-xs text-muted-foreground">🎵 {result.music_suggestion}</p>}
            <ResultRating product="script" contentId={result.id || result.title || ""} projectId={projectId} />
          </div>
        ) : <div className="flex items-center justify-center h-full text-center text-muted-foreground"><div><Film className="w-12 h-12 mx-auto mb-2 opacity-40" /><p className="text-sm">سيظهر السكربت المولّد هنا</p></div></div>}
      </div>
    </div>
  );
}

function VideoTab({ projectId, brand, extras = [], product = {}, onProduct }) {
  const [prompt, setPrompt] = useState("");
  const [aspect, setAspect] = useState("9:16");
  const [resolution, setResolution] = useState("720p");
  const [duration, setDuration] = useState("8");
  const [gender, setGender] = useState("male");
  const [pick, setPick] = useState({ animGender: "male", voice: "", style: "" });
  const [brief, setBrief] = useState(EMPTY_BRIEF);
  const [voiceName, setVoiceName] = useState("");
  const [voiceReady, setVoiceReady] = useState(false);
  const [idea, setIdea] = useState("");
  const [videoId, setVideoId] = useState(null);
  const [status, setStatus] = useState(null); // processing | completed | failed
  const [videoUrl, setVideoUrl] = useState(null);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);
  const watch = useVideoWatch(pollRef, { setStatus, setVideoUrl, setVideoId, setVoiceName, setIdea, setJob });

  useEffect(() => { resumeVideo(projectId, watch, setStatus, setJob); return () => clearInterval(pollRef.current); }, [projectId]); // eslint-disable-line

  const gen = async (premium = false) => {
    if (!prompt) return toast.error("أدخل وصف الفيديو");
    const miss = briefMissing(brief);
    if (miss.length) return toast.error(`أكمل تفاصيل الإعلان: ${miss.join("، ")}`);
    setStatus("processing"); setVideoUrl(null); setJob({ start: Date.now(), progress: 0, stage: "scene" });
    try {
      const { data } = await api.post("/content/video", {
        project_id: projectId, prompt, aspect_ratio: aspect, resolution, duration: parseInt(duration), premium: premium === true, gender, ad_brief: brief,
        voice: pick.voice, voice_style: pick.style, anim_gender: gender === "animation" ? pick.animGender : "",
        brand_name: brand?.name || "", overlay_text: brand?.info || "", extras,
      });
      toast.info("بدأ توليد الفيديو، قد يستغرق عدة دقائق…");
      watch(data.id);
    } catch (err) {
      setStatus("failed");
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 mt-6">
      <div className="glass rounded-2xl p-6 border border-border space-y-4">
        {onProduct && <ProductPanel product={product} onProduct={onProduct} testId="video-product" />}
        <Field label="وصف الفيديو"><Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4} placeholder="مثال: لقطة سينمائية لفنجان قهوة يُسكب ببطء مع بخار متصاعد وإضاءة دافئة" className="bg-secondary border-border" data-testid="video-prompt-input" /></Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="الأبعاد">
            <Select value={aspect} onValueChange={setAspect}><SelectTrigger className="bg-secondary border-border" data-testid="video-aspect-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover border-border"><SelectItem value="9:16">9:16 عمودي</SelectItem><SelectItem value="16:9">16:9 أفقي</SelectItem></SelectContent></Select>
          </Field>
          <Field label="الجودة">
            <Select value={resolution} onValueChange={setResolution}><SelectTrigger className="bg-secondary border-border" data-testid="video-resolution-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover border-border"><SelectItem value="360p">360p</SelectItem><SelectItem value="720p">720p</SelectItem><SelectItem value="1080p">1080p</SelectItem></SelectContent></Select>
          </Field>
          <Field label="المدة (ث)">
            <Select value={duration} onValueChange={setDuration}><SelectTrigger className="bg-secondary border-border" data-testid="video-duration-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover border-border">{[4,6,8].map((d) => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}</SelectContent></Select>
          </Field>
        </div>
        <AdBriefFields value={brief} onChange={setBrief} testId="video-brief" />
        <CharacterPicker value={gender} onChange={setGender} pick={pick} onPick={setPick} testId="video-character" productName={`${brief?.product_name || ""} ${brief?.product_type || ""}`.trim()}
          previewRequest={prompt.trim() ? (v, s) => api.post("/content/voice-preview", { project_id: projectId, voice: v, style: s || "", script: prompt }, { responseType: "blob", timeout: 180000 }) : undefined} />
        {prompt.trim() && <StudioVoiceBox projectId={projectId} text={prompt} voice={pick.voice} style={pick.style} free onReady={setVoiceReady} onChangeText={setPrompt} testId="video" />}
        <Button onClick={() => gen(false)} disabled={status === "processing" || !voiceReady} title={voiceReady ? "" : "جرّب الصوت واحفظه أولاً"} className="w-full bg-primary hover:bg-primary/90 text-white gap-2 disabled:opacity-50" data-testid="content-generate-video-btn">
          {status === "processing" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />} ٣· توليد الفيديو (3 لقطات + راوٍ)
        </Button>
        <p className="text-xs text-muted-foreground text-center">فيديو حركي يُبنى من وصفك بأحداث وتعابير حقيقية — الراوي ينطق جمل وصفك حرفيًا بالصوت الذي جرّبته وحفظته</p>
      </div>
      <div className="glass rounded-2xl p-6 border border-border flex items-center justify-center min-h-[320px]">
        {status === "processing" ? (
          <div className="text-center w-full"><Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" /><p className="text-sm text-muted-foreground mb-4">جارٍ إنتاج الفيديو…</p><GenProgress kind="video" job={job} /></div>
        ) : status === "completed" && videoUrl ? (
          <div className="w-full" data-testid="video-result">
            <div className="relative mb-3">
              <video src={mediaUrl(videoUrl)} controls playsInline className="rounded-xl w-full border border-border" />
              {videoId && <DeleteX testId="video-delete" className="absolute top-2 end-2" onConfirm={() => delContent(videoId, () => { setVideoUrl(null); setVideoId(null); setStatus(null); })} />}
            </div>
            {videoId && <OverlayEditor contentId={videoId} testId="video-overlay" className="w-full mb-2" onUpdated={(d) => d.url && setVideoUrl(d.url)} />}
            {voiceName && <p className="text-xs text-center text-muted-foreground mb-1" data-testid="video-voice-name">بصوت: <span className="text-primary font-bold">{voiceName}</span></p>}
            {idea && <p className="text-[11px] text-center text-muted-foreground mb-3" data-testid="video-director-idea">فكرة المخرج: {idea}</p>}
            <a href={videoUrl} target="_blank" rel="noopener noreferrer"><Button variant="outline" className="w-full gap-2 border-border"><Download className="w-4 h-4" /> فتح / تحميل الفيديو</Button></a>
            {videoId && <ResultRating product="video" contentId={videoId} projectId={projectId} />}
          </div>
        ) : status === "failed" ? (
          <div className="text-center text-destructive"><Video className="w-12 h-12 mx-auto mb-2 opacity-50" /><p className="text-sm">تعذّر توليد الفيديو، حاول مجدداً</p></div>
        ) : (
          <div className="text-center text-muted-foreground"><Video className="w-12 h-12 mx-auto mb-2 opacity-40" /><p className="text-sm">سيظهر الفيديو المولّد هنا</p></div>
        )}
      </div>
    </div>
  );
}
